/**
 * Environment fingerprint on a stored trading credential.
 *
 * `authInfo` holds an API key/secret issued by ONE backend and a signing key
 * registered against ONE chain's EIP-712 domain. It is persisted under an
 * address-keyed record in `localStorage`, which is partitioned by ORIGIN only —
 * so in local dev several environments (different checkouts, branches, chains,
 * backends) share one store and silently reuse each other's credentials.
 *
 * The result is an order signed under the current chain's domain by a key the
 * backend registered under another one: rejected as an invalid signature even
 * though the signature is correct, with nothing able to detect the mismatch.
 *
 * So the record carries the environment it was minted for. A record whose
 * fingerprint does not match — or which predates fingerprinting — is treated as
 * ABSENT, which makes `accountSigningKeyState` report
 * `REQUIRE_REGISTER_SIGNING` and lets the normal connect flow re-register.
 * One signature prompt, no migration guesswork, no instructions to clear
 * storage by hand.
 */
import { API_URL } from "../../utils/env";
import { getChainIdFromEnv } from "../../config/chains/getters";

/** Bump when the credential shape changes in a way older records cannot satisfy. */
export const CREDENTIAL_SCHEMA_VERSION = 1;

export interface CredentialFingerprint {
  v: number;
  chainId: number;
  apiUrl: string;
}

export const getCurrentFingerprint = (): CredentialFingerprint => ({
  v: CREDENTIAL_SCHEMA_VERSION,
  chainId: Number(getChainIdFromEnv()),
  apiUrl: API_URL,
});

/** Stamp a credential with the environment that minted it. */
export const withFingerprint = <T extends object>(info: T): T &
  CredentialFingerprint => ({ ...info, ...getCurrentFingerprint() });

/**
 * Is this stored record usable by the running build?
 *
 * Unversioned (pre-fingerprint) records return `false` on purpose: they carry
 * no evidence of which chain minted them, and assuming they match is exactly
 * the guess this module exists to remove.
 */
export const matchesCurrentEnvironment = (
  info: Partial<CredentialFingerprint> | undefined
): boolean => {
  if (!info) {
    return false;
  }
  const current = getCurrentFingerprint();
  return (
    info.v === current.v &&
    Number(info.chainId) === current.chainId &&
    info.apiUrl === current.apiUrl
  );
};
